import React, { createContext, useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { loader } from 'graphql.macro';
import { useQuery, useMutation } from '@apollo/client';

import { useCurrentUserContext } from 'utils/User';

const assignedAssetGroupingIdsQuery = loader(
  './assignedAssetGroupingIds.graphql',
);
const updatePreferencesMutation = loader(
  './WelcomePanel/updatePreference.graphql',
);

const AssignedToMeContext = createContext({
  showOnlyAssignedToMe: false,
  assignedAssetGroupingIds: [],
  customerIds: undefined,
  loading: false,
  customerIdsReady: true,
  toggleAssignedToMe: () => {},
  filterSuffix: '',
});

export function AssignedToMeProvider({
  children,
  showOnlyAssignedToMe: showOnlyAssignedToMeProp,
}) {
  const showOnlyAssignedToMePref = showOnlyAssignedToMeProp ?? false;
  const { id: userId } = useCurrentUserContext();

  const [updatePreference] = useMutation(updatePreferencesMutation, {
    refetchQueries: ['BusinessOverviewPreferences'],
  });

  const showOnlyAssignedToMe = showOnlyAssignedToMePref;

  const { data, loading } = useQuery(assignedAssetGroupingIdsQuery, {
    variables: { userId },
    fetchPolicy: 'cache-and-network',
  });

  // True once `customerIds` reflects the user's real assignment state, either
  // from cache or from the network. Consumers whose query is expensive on the
  // unfiltered path must wait for this instead of firing with the initial
  // `customerIds: undefined` and then refiring with the real value.
  const customerIdsReady = !loading || data !== undefined;

  const assignedAssetGroupingIds = useMemo(() => {
    if (!data?.sentinelTeam?.assignees?.collection) return [];
    return data.sentinelTeam.assignees.collection
      .map(({ assignable }) => assignable?.id)
      .filter(Boolean);
  }, [data]);

  // Effective toggle state: forced off when user has 0 assigned depots.
  const hasAssigned = assignedAssetGroupingIds.length > 0;
  const effectiveShowOnlyAssigned = showOnlyAssignedToMe && hasAssigned;

  // Derived value: the customerIds to pass to queries.
  // undefined when toggle is off; the ID array when filtering.
  const customerIds = useMemo(
    () => (effectiveShowOnlyAssigned ? assignedAssetGroupingIds : undefined),
    [effectiveShowOnlyAssigned, assignedAssetGroupingIds],
  );

  const toggleAssignedToMe = useCallback(() => {
    updatePreference({
      variables: {
        input: {
          attributes: { showOnlyAssignedToMe: !showOnlyAssignedToMe },
        },
      },
    });
  }, [showOnlyAssignedToMe, updatePreference]);

  // Appends assetGroupingIds filter to a URL path when toggle is on.
  const filterSuffix = useMemo(
    () =>
      effectiveShowOnlyAssigned && assignedAssetGroupingIds.length > 0
        ? `assetGroupingIds=${assignedAssetGroupingIds.join(',')}`
        : '',
    [effectiveShowOnlyAssigned, assignedAssetGroupingIds],
  );

  const value = useMemo(
    () => ({
      showOnlyAssignedToMe: effectiveShowOnlyAssigned,
      assignedAssetGroupingIds,
      customerIds,
      customerIdsReady,
      loading,
      toggleAssignedToMe,
      filterSuffix,
    }),
    [
      effectiveShowOnlyAssigned,
      assignedAssetGroupingIds,
      customerIds,
      customerIdsReady,
      loading,
      toggleAssignedToMe,
      filterSuffix,
    ],
  );

  return (
    <AssignedToMeContext.Provider value={value}>
      {children}
    </AssignedToMeContext.Provider>
  );
}

AssignedToMeProvider.propTypes = {
  children: PropTypes.node.isRequired,
  showOnlyAssignedToMe: PropTypes.bool,
};

export function useAssignedToMe() {
  const context = useContext(AssignedToMeContext);
  const { filterSuffix } = context;

  const buildFilteredPath = useCallback(
    (path) => {
      if (!filterSuffix) return path;
      const separator = path.includes('?') ? '&' : '?';
      return `${path}${separator}${filterSuffix}`;
    },
    [filterSuffix],
  );

  return { ...context, buildFilteredPath };
}
